import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";

import MobileBottomNav from "../components/common/MobileBottomNav";
import GenericModal from "../components/common/GenericModal";
import { User, Loader2 } from "lucide-react";
import useProfileStore from "../store/customer/useProfileStore";
import useAddressStore from "../store/useAddressStore";
import SuccessPopup from "../components/common/SuccessPopup";
import { axiosEnquiryInstance } from "../lib/axiosInstance";


const ArchiDesign = () => {
  const navigate = useNavigate();
  const { token, userType, user } = useAuthStore();
  const isCustomerLoggedIn = token && userType === "customer" && user;
  const { profile, fetchProfile } = useProfileStore();

  const [isOpen, setIsOpen] = useState(false);
  const [showFixedNav, setShowFixedNav] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);

  const [archiServices, setArchiServices] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [isLoadingProjectCategories, setIsLoadingProjectCategories] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(true);

  const {
    districts,
    taluks,
    fetchMappedDistricts,
    fetchMappedTaluks,
    isLoadingDistricts,
    isLoadingTaluks,
  } = useAddressStore();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    district: "",
    taluk: "",
    query_message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    fetchMappedDistricts("Tamil Nadu");
    const fetchArchiServices = async () => {
      setIsLoadingServices(true);
      try {
        const response = await axiosEnquiryInstance.get(
          "/architecture-design-enquiries/services?type=SERVICE"
        );
        if (response.data.success) {
          setArchiServices(response.data.services);
        } else {
          toast.error("Could not load architectural services.");
        }
      } catch (error) {
        toast.error("Failed to fetch services list.");
        console.error("Fetch Archi Services Error:", error);
      } finally {
        setIsLoadingServices(false);
      }
    };
    fetchArchiServices();

    const fetchProjectCategories = async () => {
      setIsLoadingProjectCategories(true);
      try {
        const response = await axiosEnquiryInstance.get(
          "/architecture-design-enquiries/services?type=PROJECT_CATEGORY"
        );
        if (response.data.success) {
          setProjectCategories(response.data.services);
        }
      } catch (error) {
        console.error("Failed to fetch project categories:", error);
      } finally {
        setIsLoadingProjectCategories(false);
      }
    };
    fetchProjectCategories();

    const fetchTestimonials = async () => {
      setIsLoadingTestimonials(true);
      try {
        const response = await axiosEnquiryInstance.get("/testimonials?page=archidesign");
        if (response.data.success) {
          setTestimonials(response.data.testimonials);
        }
      } catch (error) {
        console.error("Failed to fetch testimonials:", error);
      } finally {
        setIsLoadingTestimonials(false);
      }
    };
    fetchTestimonials();
  }, [fetchMappedDistricts]);

  useEffect(() => {
    if (form.district) {
      fetchMappedTaluks(form.district, "Tamil Nadu");
    } else {
      useAddressStore.setState({ taluks: [] });
    }
  }, [form.district, fetchMappedTaluks]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      newErrors.email = "Please enter a valid email address.";
    if (!form.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(form.phone))
      newErrors.phone = "Phone number must be exactly 10 digits";
    if (!form.district.trim()) newErrors.district = "District is required";
    if (!form.taluk.trim()) newErrors.taluk = "Taluk is required";
    if (selectedServiceIds.length === 0)
      newErrors.services = "Please select at least one service.";
    if (!form.query_message.trim())
      newErrors.query_message = "Message is required";
    return newErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear error for this field
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });

    if (name === "phone") {
      const digits = value.replace(/\D/g, "");
      setForm((prev) => ({ ...prev, [name]: digits }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        ...(name === "district" && { taluk: "" }),
      }));
    }
  };

  const handleServiceSelection = (serviceId) => {
    // Clear services error
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.services;
      return newErrors;
    });

    setSelectedServiceIds((prevIds) => {
      const newIds = new Set(prevIds);
      if (newIds.has(serviceId)) {
        newIds.delete(serviceId);
      } else {
        newIds.add(serviceId);
      }
      return Array.from(newIds);
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const payload = {
          ...form,
          service_ids: selectedServiceIds,
        };
        const response = await axiosEnquiryInstance.post(
          "/architecture-design-enquiries",
          payload
        );

        if (response.data.success) {
          setIsSuccessPopupOpen(true);
          setForm({
            name: "",
            email: "",
            phone: "",
            district: "",
            taluk: "",
            query_message: "",
          });
          setSelectedServiceIds([]);
          setSubmitAttempted(false);
          setErrors({});
        } else {
          toast.error(
            response.data.message || "Submission failed. Please try again."
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
          "An error occurred while submitting your enquiry."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isCustomerLoggedIn && !profile) {
      fetchProfile();
    }
  }, [isCustomerLoggedIn, profile, fetchProfile]);

  useEffect(() => {
    if (isCustomerLoggedIn && profile) {
      setForm((prev) => ({
        ...prev,
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.contact_number || "",
        district: profile.district || "",
        taluk: profile.taluk || "",
      }));
    }
  }, [isCustomerLoggedIn, profile]);

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector(".hero-section");
      if (heroSection) {
        setShowFixedNav(heroSection.getBoundingClientRect().bottom <= 0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLoginRedirect = (selectedUserType) => {
    if (token && userType) {
      if (userType !== selectedUserType) {
        toast.error(`You are already logged in as a ${userType}`);
        return;
      }
      if (userType === "builder") navigate("/builder/dashboard");
      else navigate("/customer/projectinvite");
    } else {
      navigate(`/login?type=${selectedUserType}`);
    }
    setIsOpen(false);
  };

  const handleSignupCardClick = (signupUserType) => {
    setIsSignupModalOpen(false);
    navigate(
      signupUserType === "builder" ? "/builder/signup" : "/customer/signup"
    );
  };

  const scrollToForm = () => {
    const formSection = document.getElementById("getInTouch");
    if (formSection) {
      const yOffset = -100;
      const y =
        formSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const categories = ["Residential", "Commercial", "Institutional"];
  const projects = [
    "/assets/450.jpg",
    "/assets/450.jpg",
    "/assets/450.jpg",
    "/assets/450.jpg",
    "/assets/450.jpg",
    "/assets/450.jpg",
  ];
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    if (projectCategories.length > 0 && activeCategory === null) {
      setActiveCategory(projectCategories[0].id);
    }
  }, [projectCategories, activeCategory]);

  const getImageUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    const baseURL = axiosEnquiryInstance.defaults.baseURL.replace("/api/v2", "");
    const normalizedPath = path.replace(/\\/g, "/");
    const finalPath = normalizedPath.startsWith("/") ? normalizedPath : "/" + normalizedPath;
    return `${baseURL}${finalPath}`;
  };

  const activeCategoryImages = projectCategories.find(s => s.id === activeCategory)?.images || [];

  const [showAllServices, setShowAllServices] = useState(false);

  const visibleServices = showAllServices
    ? archiServices
    : archiServices.slice(0, 6);

  const navLinks = [
    { href: "/", text: "HOME" },
    {
      href: "/#services",
      text: "SERVICES",
      onClick: () => {
        const servicesSection = document.querySelector("#services");
        if (servicesSection) {
          const yOffset =
            servicesSection.getBoundingClientRect().top +
            window.pageYOffset -
            100;
          window.scrollTo({ top: yOffset, behavior: "smooth" });
        }
      },
    },
    {
      isButton: true,
      text: "JOIN US",
      onClick: () => setIsSignupModalOpen(true),
    },
    // { href: "/job-portal", text: "JOB PORTAL" },
    { href: "/support", text: "SUPPORT" },
  ];

  const renderNavLinks = (isMobile = false, isFixed = false) =>
    navLinks.map((link, index) =>
      link.isButton ? (
        <button
          key={index}
          onClick={link.onClick}
          className={
            isMobile
              ? "block w-full text-left px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md"
              : `relative px-4 py-2 text-[14px] font-bold transition-all hover:rounded-full duration-300 ${isFixed
                ? "text-black hover:text-gray-500 hover:bg-gray-100"
                : "text-white hover:text-gray-300"
              }`
          }
        >
          {link.text}
        </button>
      ) : (
        <a
          key={index}
          href={link.href}
          onClick={(e) => {
            e.preventDefault();
            if (link.href.startsWith("#")) {
              const element = document.querySelector(link.href);
              if (element) {
                const yOffset =
                  element.getBoundingClientRect().top +
                  window.pageYOffset -
                  100;
                window.scrollTo({ top: yOffset, behavior: "smooth" });
              }
            } else {
              navigate(link.href);
            }
            if (isMobile) setIsOpen(false);
          }}
          className={
            isMobile
              ? "block px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md"
              : `relative px-4 py-2 text-[14px] font-bold transition-all hover:rounded-full duration-300 ${isFixed
                ? "text-black hover:text-gray-500 hover:bg-gray-100"
                : "text-white hover:text-gray-300"
              }`
          }
        >
          {link.text}
        </a>
      )
    );

  const renderCustomerButton = (isMobile = false, isFixed = false) =>
    token && userType === "customer" && user ? (
      <button
        onClick={() => navigate("/customer/profile")}
        className={
          isMobile
            ? "flex items-center gap-2 w-full text-left px-3 py-2 bg-blue-600 text-white font-medium hover:bg-blue-700 rounded-md"
            : `flex items-center gap-2 bg-blue-600 text-white font-bold px-4 py-2 rounded-full hover:bg-blue-700`
        }
      >
        <User className="w-4 h-4" />
        Hello, {user.name.split(" ")[0]}
      </button>
    ) : (
      <button
        onClick={() => handleLoginRedirect("customer")}
        className={
          isMobile
            ? "block w-full text-left px-3 py-2 text-black font-medium hover:bg-gray-100 rounded-md"
            : "bg-blue-600 text-white font-bold px-6 py-2 rounded-full hover:bg-blue-700"
        }
        style={!isMobile && !isFixed ? { marginLeft: "60px" } : {}}
      >
        Customer Login
      </button>
    );

  return (
    <div className="w-full pb-16 lg:pb-0">
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={() => setIsSuccessPopupOpen(false)}
        message="Thank you for your enquiry! Our design team will contact you within 24 hours."
      />
      <GenericModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        title="Join Ducktail As"
        size="max-w-lg"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
          <div
            onClick={() => handleSignupCardClick("builder")}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
          >
            <img
              src="/assets/builder.png"
              alt="Builder"
              className="w-24 h-24 object-contain mb-3"
            />
            <h3 className="text-md font-semibold text-gray-700">Builder</h3>
            <p className="text-xs text-gray-500 text-center mt-1">
              Showcase your projects and connect with clients.
            </p>
          </div>
          <div
            onClick={() => handleSignupCardClick("customer")}
            className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
          >
            <img
              src="/assets/young-man.png"
              alt="Customer"
              className="w-24 h-24 object-contain mb-3"
            />
            <h3 className="text-md font-semibold text-gray-700">Customer</h3>
            <p className="text-xs text-gray-500 text-center mt-1">
              Find verified builders for your dream project.
            </p>
          </div>
        </div>
      </GenericModal>

      {showFixedNav && (
        <div
          className="fixed top-0 left-0 right-0 bg-white shadow-md"
          style={{ zIndex: 9998 }}
        >
          <div className="max-w-[90rem] mx-auto px-2  sm:px-4 lg:px-6">
            <div className="flex justify-between items-center h-16">
              <div
                className="flex-shrink-0 cursor-pointer"
                onClick={() => navigate("/")}
              >
                <img
                  src="/assets/black logo.png"
                  alt="Ducktail"
                  className="h-12 sm:h-12"
                />
              </div>
              <div className="hidden lg:flex space-x-3 items-center">
                {renderNavLinks(false, true)}
                {renderCustomerButton(false, true)}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="hero-section h-[66vh] flex flex-col items-center justify-center text-center bg-cover relative"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {/* Desktop Background Image */}
          <img
            src="/assets/archi design.jpg"
            alt="Architectural Design"
            className="absolute inset-0 w-full h-full object-cover object-center hidden sm:block"
          />
          {/* Mobile Background Image */}
          <img
            src="/assets/Archi.jpg"
            alt="Architectural Design"
            className="absolute inset-0 w-full h-full object-cover object-center block sm:hidden"
          />
        </div>
        <div className="absolute inset-0 bg-black/75"></div>
        <div className="absolute top-0 left-0 right-0 z-30">
          <div className="bg-transparent">
            <div className="max-w-[90rem] mx-auto px-2  sm:px-4 lg:px-6">
              <div className="flex justify-between items-center h-16">
                <div
                  className="flex-shrink-0 cursor-pointer"
                  onClick={() => navigate("/")}
                >
                  <img
                    src="/assets/Transparent background.png"
                    alt="Ducktail"
                    className="h-16 sm:h-18 lg:h-20"
                  />
                </div>
                <div className="hidden lg:flex space-x-3 items-center">
                  {renderNavLinks()}
                  {renderCustomerButton()}
                </div>
                <div className="hidden"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-5xl text-white flex flex-col items-center justify-center h-full text-center p-4">
          <h1 className="text-xl md:text-2xl font-bold uppercase transition-all duration-300">
            {" "}
            <span className="text-yellow-400"> Architectural </span>
            Design
          </h1>
          <p className="mt-4 text-xs md:text-sm text-gray-300">
            Our expert's  architects create functional, beautiful spaces that
            reflect your vision and lifestyle. Let us bring your dream home to
            life with designs that perfectly blend style and practicality.
          </p>
          <button
            onClick={scrollToForm}
            className="mt-6 bg-blue-700 text-white px-8 py-3 rounded-md hover:bg-blue-800 transition duration-300"
          >
            REQUEST SERVICE
          </button>
        </div>
      </div>

      <div className="relative flex flex-col md:flex-row items-center justify-center px-6 md:px-16 py-12">
        <div className="w-full md:w-1/2 relative">
          <img
            src="/assets/archi sketch.png"
            alt="Interior Design"
            className="w-full h-auto rounded-[20px]"
          />
        </div>
        <div className="w-full md:w-1/2 bg-white shadow-lg p-8 md:p-12 relative md:-ml-20 md:mt-0 mt-6 rounded-lg">
          <p className="text-gray-500 text-xs uppercase">Welcome to Archi</p>
          <h2 className="text-xl md:text-2xl font-bold mt-2 leading-tight uppercase text-blue-600">
            Designing Dreams, <br /> Building Realities
          </h2>
          <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
            At Archi Design, we turn visions into well-crafted spaces with
            innovative architectural solutions. From concept to execution, we
            deliver detailed and aesthetically refined designs. Our expertise in
            2D planning, 3D elevations, walkthroughs, and structural planning
            ensures precision, functionality, and excellence in every project.
          </p>
        </div>
      </div>

      <div id="getInTouch" className="px-6 md:px-16 py-12 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12">
            <div className="w-full md:w-1/3">
              <h2 className="text-xl md:text-2xl font-bold mb-6 uppercase text-blue-600">GET IN TOUCH</h2>
              <div className="mt-6">
                <h3 className="text-xl font-bold">Ducktail</h3>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center">
                    <span className="text-red-500 mr-2">📞</span>
                    <a href="tel:+918500890077" className="">
                      +91 85008 90077
                    </a>
                  </div>
                  <div className="flex items-center">
                    <span className="text-red-500 mr-2">✉️</span>
                    <a href="mailto:archiinfo@ducktail.in" className="">
                      archiinfo@ducktail.in
                    </a>
                  </div>
                  <div className="flex items-center">
                    <span className="text-red-500 mr-2">📍</span>
                    <p>Tamil Nadu</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full md:w-3/5 bg-white p-6 rounded-lg shadow-lg ml-auto">
              <form onSubmit={handleFormSubmit} className="space-y-3 text-sm">
                <div className="flex flex-col md:grid md:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleInputChange}
                      placeholder="Your Name *"
                      className={`border p-2 w-full rounded-[4px] ${submitAttempted && errors.name
                        ? "border-red-500"
                        : "border-gray-300"
                        }`}
                      readOnly={isCustomerLoggedIn}
                    />
                    {submitAttempted && errors.name && (
                      <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleInputChange}
                      placeholder="Your Email *"
                      className={`border p-2 w-full rounded-[4px] ${submitAttempted && errors.email
                        ? "border-red-500"
                        : "border-gray-300"
                        }`}
                      readOnly={isCustomerLoggedIn}
                    />
                    {submitAttempted && errors.email && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                        +91
                      </span>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleInputChange}
                        maxLength={10}
                        placeholder="Phone Number *"
                        className={`border p-2 w-full pl-10 rounded-[4px] ${submitAttempted && errors.phone
                          ? "border-red-500"
                          : "border-gray-300"
                          }`}
                        readOnly={isCustomerLoggedIn}
                      />
                    </div>
                    {submitAttempted && errors.phone && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    {isCustomerLoggedIn ? (
                      <input
                        type="text"
                        value={profile?.district || ""}
                        readOnly
                        className="border p-2 w-full bg-gray-100 text-gray-700 rounded-[4px]"
                      />
                    ) : (
                      <select
                        name="district"
                        value={form.district}
                        onChange={handleInputChange}
                        className={`border p-2 w-full bg-white rounded-[4px] ${submitAttempted && errors.district
                          ? "border-red-500"
                          : "border-gray-300"
                          }`}
                        disabled={isLoadingDistricts || isSubmitting}
                      >
                        <option value="">
                          {isLoadingDistricts
                            ? "Loading..."
                            : "Select District *"}
                        </option>
                        {districts?.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    )}
                    {submitAttempted && errors.district && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.district}
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    {isCustomerLoggedIn ? (
                      <input
                        type="text"
                        value={profile?.taluk || ""}
                        readOnly
                        className="border p-2 w-full bg-gray-100 text-gray-700 rounded-[4px]"
                      />
                    ) : (
                      <select
                        name="taluk"
                        value={form.taluk}
                        onChange={handleInputChange}
                        className={`border p-2 w-full bg-white rounded-[4px] ${submitAttempted && errors.taluk
                          ? "border-red-500"
                          : "border-gray-300"
                          }`}
                        disabled={
                          !form.district || isLoadingTaluks || isSubmitting
                        }
                      >
                        <option value="">
                          {isLoadingTaluks ? "Loading..." : "Select Taluk *"}
                        </option>
                        {taluks?.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}
                    {submitAttempted && errors.taluk && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.taluk}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Services You're Interested In{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  {isLoadingServices ? (
                    <p className="text-xs text-gray-500">Loading services...</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {archiServices.map((service) => (
                        <label
                          key={service.id}
                          className="flex items-center space-x-2 cursor-pointer p-2 rounded-md hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedServiceIds.includes(service.id)}
                            onChange={() => handleServiceSelection(service.id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-700">
                            {service.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  {submitAttempted && errors.services && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.services}
                    </p>
                  )}
                </div>
                <textarea
                  name="query_message"
                  value={form.query_message}
                  onChange={handleInputChange}
                  className={`border p-2 w-full rounded-[4px] ${submitAttempted && errors.query_message
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  placeholder="Your Message *"
                  rows="3"
                ></textarea>
                {submitAttempted && errors.query_message && (
                  <p className="text-xs text-red-500 mt-1">
                    {errors.query_message}
                  </p>
                )}
                <button
                  type="submit"
                  className="w-full bg-black text-white p-2 hover:bg-gray-800 transition uppercase text-sm flex items-center justify-center disabled:opacity-60"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Request Service"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 md:px-16 py-12">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold min-w-[200px] mx-auto">SERVICES</h2>
          <p className="text-gray-600 mt-2 max-w-lg mx-auto text-sm">
            We offer a wide range of architectural design services, combining
            creativity and technical expertise to bring your vision to life.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {visibleServices.map((service) => (
            <div
              key={service.id}
              className="relative p-6 bg-white shadow-lg flex flex-col items-start transition hover:bg-black hover:text-white hover:cursor-pointer"
            >
              <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition"></div>
              <div className="relative z-10">
                <h3 className="mt-2 text-base font-bold">{service.name}</h3>
                <p className="mt-1 text-xs">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
        {archiServices.length > 6 && (
          <div className="text-center mt-6">
            <button
              onClick={() => setShowAllServices(!showAllServices)}
              className="text-blue-600 font-semibold hover:underline"
            >
              {showAllServices ? "Show Less" : "Show More"}
            </button>
          </div>
        )}
      </div>
      <div className="px-4 md:px-16 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold">LATEST PROJECTS</h2>
          <p className="text-gray-600 mt-2 max-w-lg mx-auto">
            Our latest projects reflect our commitment to precision, aesthetics,
            and functionality, turning visions into reality.
          </p>
        </div>
        {/* Responsive, centered, horizontally scrollable categories with hidden scrollbar */}
        <div className="w-full mb-6">
          <div
            className="flex justify-center md:justify-center gap-2 overflow-x-auto pb-2 hide-scrollbar"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <style>
              {`
                .hide-scrollbar::-webkit-scrollbar {
                  display: none;
                  height: 0;
                  background: transparent;
                }
                @media (max-width: 768px) {
                  .category-btn {
                    font-size: 14px;
                    padding-left: 14px;
                    padding-right: 14px;
                  }
                  .category-scroll-center {
                    justify-content: center;
                  }
                }
              `}
            </style>
            <div className="flex gap-2 w-full category-scroll-center justify-center">
              {projectCategories.map((category, index) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`category-btn flex-shrink-0 px-4 py-2 border transition whitespace-nowrap rounded-t-md border-b-2 select-none ${activeCategory === category.id
                    ? "bg-black text-white border-black"
                    : "bg-white text-black border-black"
                    }`}
                  style={{
                    minWidth: "110px",
                    fontWeight: activeCategory === category.id ? "bold" : "normal",
                    outline: "none",
                  }}
                  tabIndex={0}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        {/* Responsive grid, original image size */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-2">
          {activeCategoryImages.length > 0 ? (
            activeCategoryImages.map((img, index) => (
              <div
                key={img.id}
                className="w-full aspect-square rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 mx-auto"
              >
                <img
                  src={getImageUrl(img.image_path)}
                  alt={`Project ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))
          ) : (
            <div className="col-span-full py-20 text-center text-gray-500">
              No projects found for this category yet.
            </div>
          )}
        </div>
      </div>

      <section className="bg-white py-2">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-blue-700 mb-2 text-center">
            OUR TESTIMONIALS
          </h2>
          {!isLoadingTestimonials && testimonials.length > 0 && (
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              loop={testimonials.length > 1}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 2,
                  spaceBetween: 20,
                },
                1024: {
                  slidesPerView:
                    testimonials.length > 2 ? 3 : testimonials.length,
                  spaceBetween: 30,
                },
              }}
              className="pb-2 h-80"
            >
              {testimonials.map((testimonial) => (
                <SwiperSlide key={testimonial.id}>
                  <div className="bg-gray-100 rounded-2xl border border-gray-200 shadow-md p-8 h-72 flex flex-col text-center">
                    <div className="flex-grow flex items-center justify-center">
                      <p className="text-sm text-gray-700 italic leading-relaxed">
                        "{testimonial.text}"
                      </p>
                    </div>
                    <div className="mt-auto">
                      <div className="flex justify-center mb-2">
                        {Array.from({ length: testimonial.rating }, (_, i) => (
                          <svg
                            key={i}
                            className="w-5 h-5 text-orange-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M10 15l-5.09 3.26 1.93-6.36-5.09-3.74h6.3L10 2.5 12.95 8.16h6.3l-5.09 3.74 1.93 6.36z" />
                          </svg>
                        ))}
                      </div>
                      <h4 className="text-md font-bold text-gray-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </section>

      <MobileBottomNav />
      <div className="md:pb-0">

      </div>
    </div>
  );
};

export default ArchiDesign;
